import React, { useEffect, useState } from 'react'
import View from '../View'
import { Checkbox, Grid } from '@mui/material'
import AlertSnackbar, {
    alertSnackbarContentProps,
} from 'components/AlertSnackbar'
import idDirectory from './idAttributes'
import { AccentArea, AccountHistory, Alert, PasswordChange } from 'components'
import ProfileApi from 'api/ProfileApi'
import { withRequiredRole } from 'components/useRequireRole'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import EntFeature from 'models/EntFeature'
import logger from 'utils/logger'
import '../views.css'
import { iconsTheme } from 'theme-exports'
import Icon from 'components/Icon'
import { useUiStateContext } from 'context/UiState/UiStateContext'
import NotificationsArea from './NotificationsArea/NotificationsArea'
import UsersApi from 'api/UsersApi'
import UpdateEmail from 'components/UpdateEmail'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { ISwitchListOption } from 'components/SwitchList/SwitchList'

interface Notification {
    message: string
    severity: 'error' | 'success' | 'info' | 'warning' | undefined
}

/**
 * The Profile settings view for a person, with their account information
 */
export const Settings: React.FC = () => {
    const { title_icon: titleIconEnabled } = useFeatureToggle('ACCENT_AREA')
    const { user, loading, refreshAuthedUser } = useAuthedUser()
    const isFederatedUser = user?.is_federated
    const showClientTypeIssuerUI = user?.is_issuer ?? false

    // Disabling for these 4 users (68: sabre-partner-admin, 35: sabre_admin, 91: outpayce_demo, and 86 and 87 for Monavate) and  for demo:
    const disabledUser = [35, 68, 86, 87, 91].includes(user?.id ?? 0)

    const [
        alertNotification,
        setAlertNotification,
    ] = React.useState<Notification>()
    const [showUniqueLogins, setShowUniqueLogins] = React.useState<boolean>(
        false
    )
    const [emailSettings, setEmailSettings] = useState<ISwitchListOption[]>([])
    const [loadingEmailSettings, setLoadingEmailSettings] = useState<boolean>(
        false
    )
    const [updatingEmailSettings, setUpdatingEmailSettings] = useState<boolean>(
        false
    )
    const [alertSnackbarMainOpen, setAlertSnackbarMainOpen] = useState<boolean>(
        false
    )
    const [
        alertSnackbarMainProps,
        setAlertSnackbarMainProps,
    ] = useState<alertSnackbarContentProps>({})

    const { invalidate } = useUiStateContext()

    useEffect(() => {
        if (refreshAuthedUser) refreshAuthedUser()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (user) obtainEmailSettings(user)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id])

    const obtainEmailSettings = (user: any) => {
        // Load in the profile api info
        setLoadingEmailSettings(true)
        ProfileApi.getNotifications(user?.allow_resources)
            .then((data) => {
                console.log('data', data)
                // intercept "labels" for issuer
                if (showClientTypeIssuerUI) {
                    const stringToReplace = 'Chargebacks'
                    const stringToReplaceWith = 'Cases Created'
                    data.forEach((item: any) => {
                        item.name = item.name.replace(
                            stringToReplace,
                            stringToReplaceWith
                        )
                        item.label = item.label.replace(
                            stringToReplace,
                            stringToReplaceWith
                        )
                    })
                }
                console.log('data', data)

                setEmailSettings(data)
            })
            .catch(() => {
                setAlertNotification({
                    message:
                        'Unable to get user settings. Please try again later.',
                    severity: 'error',
                })
                setAlertSnackbarMainProps({
                    title: 'Error',
                    message: `An error occurred while loading notifications. Please try again later.`,
                    intent: 'error',
                })
                setAlertSnackbarMainOpen(true)
            })
            .finally(() => {
                setLoadingEmailSettings(false)
            })
    }

    // Change my own password
    const handlePasswordChange = ({
        currentPassword,
        newPassword,
    }: {
        currentPassword: string
        newPassword: string
        confirmPassword: string
    }): Promise<void> => {
        return ProfileApi.setCognitoPassword(currentPassword, newPassword)
            .then((data) => {
                setAlertSnackbarMainProps({
                    title: 'Success',
                    message: 'Password was successfully updated.',
                    intent: 'success',
                })
                setAlertSnackbarMainOpen(true)
                logger(data)
            })
            .catch((error) => {
                setAlertSnackbarMainProps({
                    title: 'Error',
                    message: error?.message,
                    intent: 'error',
                })
                setAlertSnackbarMainOpen(true)
                logger(error?.message)
            })
    }

    const handleNotificationChange = (item: ISwitchListOption) => {
        const updatedEmailSettings = emailSettings.map(
            (notification: ISwitchListOption) =>
                item.id === notification.id
                    ? {
                          ...notification,
                          isChecked: !item.isChecked,
                      }
                    : notification
        )
        setUpdatingEmailSettings(true)
        ProfileApi.setNotificationSetting(user?.id, item.id, !item.isChecked)
            .then(() => {
                setEmailSettings(updatedEmailSettings)

                if (refreshAuthedUser) refreshAuthedUser()
                setAlertSnackbarMainProps({
                    title: 'Success',
                    message: 'Notification was successfully updated.',
                    intent: 'success',
                })
            })
            .catch(() => {
                setAlertSnackbarMainProps({
                    title: 'Error',
                    message:
                        'An error occurred updating notification. Please try again later.',
                    intent: 'error',
                })
            })
            .finally(() => {
                setAlertSnackbarMainOpen(true)
                setUpdatingEmailSettings(false)
            })
    }

    const handleEmailChange = (email: string) => {
        if (user && user.id) {
            return UsersApi.editEmail(user?.id, email)
                .then(() => {
                    setAlertSnackbarMainProps({
                        title: 'Success',
                        message: 'Your email has been successfully updated.',
                        intent: 'success',
                    })
                    setAlertSnackbarMainOpen(true)
                })
                .catch((e) => {
                    setAlertSnackbarMainProps({
                        title: 'Error',
                        message: 'There was an error changing your email.',
                        intent: 'error',
                    })
                    setAlertSnackbarMainOpen(true)
                    logger(e?.message)
                })
                .finally(() => {
                    ProfileApi.refreshTokens().then(() => {
                        setTimeout(() => {
                            if (refreshAuthedUser) refreshAuthedUser()
                            invalidate('whoami')
                        }, 1000)
                    })
                })
        } else {
            setAlertSnackbarMainProps({
                title: 'Error',
                message: 'There was an error changing your email.',
                intent: 'error',
            })
            return setAlertSnackbarMainOpen(true)
        }
    }

    return (
        <View title="Settings" breadcrumb="Settings" testId="settings">
            {alertNotification?.message && (
                <Alert
                    severity={alertNotification?.severity}
                    style={{ marginBottom: 10 }}
                >
                    {alertNotification?.message}
                </Alert>
            )}
            <Grid id={idDirectory.gridContainer} container spacing={4}>
                <Grid item xs={12} md={6} id={`${idDirectory.gridItem}-left`}>
                    <AccentArea
                        title={
                            <div className={'emp-icon'}>
                                {titleIconEnabled && (
                                    <Icon
                                        className={`${
                                            iconsTheme.singleConsumer
                                        } ${'emp-marginRight'}`}
                                    />
                                )}
                                <span>User Settings</span>
                            </div>
                        }
                        testId="userSettings"
                    >
                        {!loading && (
                            <section className={'emp-ladder'}>
                                <div className={'emp-lead'}>
                                    <UpdateEmail
                                        className={'emp-outlinedInputs'}
                                        onSubmit={handleEmailChange}
                                        initialEmail={user?.email}
                                        isFederated={isFederatedUser}
                                        disabledUser={disabledUser}
                                    />
                                </div>
                            </section>
                        )}
                        <section className={'emp-ladder'}>
                            <div className={'emp-lead'}>
                                <PasswordChange
                                    color={'secondary'}
                                    className={'emp-outlinedInputs'}
                                    onSubmit={handlePasswordChange}
                                    isFederated={isFederatedUser}
                                    disabledUser={disabledUser}
                                />
                            </div>
                        </section>
                    </AccentArea>
                </Grid>
                <Grid item xs={12} md={6} id={`${idDirectory.gridItem}-right`}>
                    {emailSettings.length ? (
                        <Grid item style={{ marginBottom: 20 }}>
                            <AccentArea
                                title={
                                    <div className={'emp-icon'}>
                                        {titleIconEnabled && (
                                            <Icon
                                                className={`${
                                                    iconsTheme.notification
                                                } ${'emp-marginRight'}`}
                                            />
                                        )}
                                        <span>Notifications</span>
                                    </div>
                                }
                                testId="notifications"
                            >
                                <NotificationsArea
                                    emailSettings={emailSettings}
                                    loadingEmailSettings={loadingEmailSettings}
                                    updatingEmailSettings={
                                        updatingEmailSettings
                                    }
                                    expectedLength={4}
                                    handleChange={handleNotificationChange}
                                />
                            </AccentArea>
                        </Grid>
                    ) : (
                        <></>
                    )}

                    <Grid item id={`${idDirectory.gridItem}-3`}>
                        <AccentArea
                            className={'emp-historySection'}
                            title={
                                <div className={'emp-icon'}>
                                    {titleIconEnabled && (
                                        <Icon
                                            className={`${
                                                iconsTheme.clock
                                            } ${'emp-marginRight'}`}
                                        />
                                    )}
                                    <span>Login History</span>
                                </div>
                            }
                            headerAction={
                                <label
                                    style={{
                                        display: 'flex',
                                        placeItems: 'center',
                                        fontSize: '14px',
                                    }}
                                >
                                    <Checkbox
                                        className="reduce-vertical-padding"
                                        checked={showUniqueLogins}
                                        onChange={() =>
                                            setShowUniqueLogins(
                                                !showUniqueLogins
                                            )
                                        }
                                        color="secondary"
                                        id={idDirectory.checkboxUnique}
                                    />{' '}
                                    Show Unique Only
                                </label>
                            }
                            testId="loginHx"
                        >
                            {user && user.id && (
                                <AccountHistory
                                    userId={user.id}
                                    showUniqueLogins={showUniqueLogins}
                                />
                            )}
                        </AccentArea>
                    </Grid>
                </Grid>
            </Grid>
            <AlertSnackbar
                content={alertSnackbarMainProps}
                open={alertSnackbarMainOpen}
                onClose={() => setAlertSnackbarMainOpen(false)}
                showCloseIcon
            />
        </View>
    )
}

export default withRequiredRole(EntFeature.PROFILE, Settings)
